import {
  AppliedFilterInterface,
  BlockStack,
  ChoiceList,
  FilterInterface,
  TextField,
} from '@shopify/polaris'
import { useCallback } from 'react'

import { SubscriptionFilter } from '../api'

type PriceType = 'total' | 'subtotal' | 'delivery'

const priceTypeLabel: Record<PriceType, string> = {
  total: 'Total price',
  subtotal: 'Subtotal price',
  delivery: 'Delivery price',
}

const parsePrice = (value: string): number | undefined => {
  if (value === '') {
    return undefined
  }
  const parsed = parseFloat(value)
  return Number.isFinite(parsed) && parsed >= 0 ? parsed : undefined
}

const formatBound = (value: number | undefined): string =>
  value === undefined ? '' : String(value)

const priceRangeLabel = (min: number | undefined, max: number | undefined): string | null => {
  if (min !== undefined && max !== undefined) {
    return `${min} – ${max}`
  }
  if (min !== undefined) {
    return `≥ ${min}`
  }
  if (max !== undefined) {
    return `≤ ${max}`
  }
  return null
}

export const useAddPriceFilter = (
  filter: SubscriptionFilter,
  updateFilter: (newFilter: Partial<SubscriptionFilter>) => any,
  filters: FilterInterface[],
  appliedFilters: AppliedFilterInterface[]
) => {
  const priceType = filter.price_type as PriceType | undefined

  const setPriceType = useCallback(
    (sel: string[]) => {
      const next = sel[0] as PriceType | undefined
      if (!next) {
        updateFilter({ price_type: undefined, price_min: undefined, price_max: undefined })
        return
      }
      updateFilter({ price_type: next })
    },
    [updateFilter]
  )

  const setPriceMin = useCallback(
    (value: string) => updateFilter({ price_min: parsePrice(value) }),
    [updateFilter]
  )

  const setPriceMax = useCallback(
    (value: string) => updateFilter({ price_max: parsePrice(value) }),
    [updateFilter]
  )

  const renderRangeInputs = (selected: boolean) => {
    if (!selected) {
      return null
    }

    return (
      <BlockStack gap="100">
        <TextField
          label="Minimum"
          type="number"
          min={0}
          step={0.01}
          autoComplete="off"
          value={formatBound(filter.price_min)}
          onChange={setPriceMin}
          placeholder="Min"
        />
        <TextField
          label="Maximum"
          type="number"
          min={0}
          step={0.01}
          autoComplete="off"
          value={formatBound(filter.price_max)}
          onChange={setPriceMax}
          placeholder="Max"
        />
      </BlockStack>
    )
  }

  const choices = [
    { value: 'total', label: priceTypeLabel.total, renderChildren: renderRangeInputs },
    { value: 'subtotal', label: priceTypeLabel.subtotal, renderChildren: renderRangeInputs },
    { value: 'delivery', label: priceTypeLabel.delivery, renderChildren: renderRangeInputs },
  ]

  filters.push({
    key: 'price',
    label: 'Price',
    filter: (
      <ChoiceList
        title="Price"
        titleHidden
        choices={choices}
        selected={priceType ? [priceType] : []}
        onChange={setPriceType}
      />
    ),
  })

  if (priceType) {
    const range = priceRangeLabel(filter.price_min, filter.price_max)
    if (range !== null) {
      appliedFilters.push({
        key: 'price',
        label: `${priceTypeLabel[priceType]}: ${range}`,
        onRemove: () =>
          updateFilter({
            price_type: undefined,
            price_min: undefined,
            price_max: undefined,
          }),
      })
    }
  }
}
